'use client';

import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useTenancy } from '@/contexts/TenancyContext';
import type { ChecklistTemplate } from '../types/checklist.types';

// Default templates that come with the system
const DEFAULT_TEMPLATES: ChecklistTemplate[] = [
  {
    id: 'security-check',
    createdAt: new Date(),
    updatedAt: new Date(),
    templateId: 'security-check',
    title: 'End of Day Security Check',
    description: 'Security check for office premises at the end of the working day.',
    standard: 'ISO 27001',
    isReusable: true,
    items: [
      {
        id: 'item1',
        title: 'All doors and windows are locked',
        control: 'A.11.1.2 - Physical entry controls',
        controlOptions: [
          'A.11.1.2 - Physical entry controls',
          'A.11.1.1 - Physical security perimeter',
          'A.11.1.3 - Securing offices, rooms and facilities'
        ],
        fieldType: 'Checkbox',
        requireEvidence: true
      },
      // More default items...
    ],
    createdBy: 'system',
    tenantId: 'system',
    badgeClass: 'bg-success'
  },
  // More default templates...
];

export function useTemplates() {
  const [templates, setTemplates] = useState<ChecklistTemplate[]>(DEFAULT_TEMPLATES);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { user } = useAuth();
  const { activeTenancy } = useTenancy();

  useEffect(() => {
    if (!user || !activeTenancy) {
      setLoading(false);
      return;
    }

    setLoading(true);
    
    try {
      const templatesQuery = query(
        collection(db, 'customers', activeTenancy.id, 'templates'),
        orderBy('title')
      );

      const unsubscribe = onSnapshot(
        templatesQuery,
        (snapshot) => {
          const templatesData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            // Convert Firestore timestamps to Date objects
            createdAt: doc.data().createdAt?.toDate() || new Date(),
            updatedAt: doc.data().updatedAt?.toDate() || new Date(),
          })) as ChecklistTemplate[];
          
          // Combine with default templates, ensuring no duplicates
          const allTemplates = [
            ...DEFAULT_TEMPLATES,
            ...templatesData.filter(t => !DEFAULT_TEMPLATES.some(dt => dt.id === t.id))
          ];
          
          setTemplates(allTemplates);
          setLoading(false);
        },
        (err) => {
          console.error('Error fetching templates:', err);
          setError(err);
          setLoading(false);
        }
      );

      return () => unsubscribe();
    } catch (err) {
      console.error('Error setting up template listener:', err);
      setError(err instanceof Error ? err : new Error('Failed to load templates'));
      setLoading(false);
    }
  }, [user, activeTenancy]);

  return { templates, loading, error };
}
